import { setFormHash, setFormSubmitted, setOtpValidated } from '../features/signup/signupSlice';
import { getCountryInfo } from '../helper/PhoneNumber';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { toSnakeCase } from '../helper/DataFormatter';
import { useTranslation } from 'react-i18next';
import Request from '../helper/Request';
import { getFormHash, getRecaptchaToken } from "../store";
import getLocale from '../helper/Locale';
import { SHA256 } from 'crypto-js';
import TagManager from 'react-gtm-module';


// noinspection JSUnresolvedReference
const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
const initialFormValues = {
  acceptTos: false,
  businessEmail: '',
  companyName: '',
  countryCode: 'US',
  formSubmitted: false,
  fullName: '',
  industry: 'furniture',
  language: getLocale(),
  password: '',
  dialCode: 'CA',
  phoneNumber: '',
  success: false,
  timezone: timeZone,
  volume: undefined,
};

const PostContactForm = async (
  values,
  successCallback,
  errorCallback
) => {
  const headers = { [process.env.REACT_APP_RECAPTCHA_SITE_ID]: getRecaptchaToken() };
  Request.post('', toSnakeCase(values), {
    headers
  })
    .then(() => successCallback())
    .catch((response) => errorCallback(response));
};

const PostContactSaleLead = async (
  values,
  errorCallback
) => {
  const headers = { [process.env.REACT_APP_RECAPTCHA_SITE_ID]: getRecaptchaToken() };
  Request.post('/createSalesLead', toSnakeCase(values), {
    headers
  })
    .catch((response) => errorCallback(response));
};

export const useFormControls = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [values, setValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    if ("fullName" in fieldValues) {
      temp.fullName = fieldValues.fullName ? "" : t('required_field');
      if (fieldValues.fullName) {
        temp.fullName = fieldValues.fullName.length >= 2 ? "" : t('required_full_name_length');
      }
    }

    if ("companyName" in fieldValues) {
      temp.companyName = fieldValues.companyName ? "" : t('required_field');
      if (fieldValues.companyName) {
        temp.companyName = fieldValues.companyName.length >= 2 ? "" : t('required_company_name_length');
      }
    }

    if ("businessEmail" in fieldValues) {
      temp.businessEmail = fieldValues.businessEmail ? "" : t('required_field');
      if (fieldValues.businessEmail)
        temp.businessEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(fieldValues.businessEmail)
          ? "" : t('email_required_field');
    }

    if ("password" in fieldValues) {
      temp.password = fieldValues.password ? "" : t('password_guidelines', { 'length': 10 });
      if (fieldValues.password) {
        const passwordRegex = /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{10,}$/;
        temp.password = passwordRegex.test(fieldValues.password) ? "" : t('password_guidelines', { 'length': 10 });
      }
    }

    if ("phoneNumber" in fieldValues) {
      temp.phoneNumber = fieldValues.phoneNumber ? "" : t('required_field');
      if (fieldValues.phoneNumber) {
        const phoneRegex = /^[(]?[0-9]{0,3}[)]?[-\s.]?[0-9]{3,4}[-\s.]?[0-9]{4,}$/g;
        phoneRegex.test(fieldValues.phoneNumber) ? temp.phoneNumber = '' : temp.phoneNumber = t('phone_number_field');
      }
    }

    if ("volume" in fieldValues) {
      temp.volume = fieldValues.volume ? "" : t('required_field');
    }

    if ("acceptTos" in fieldValues) {
      temp.acceptTos = fieldValues.acceptTos ? "" : t('required_field');
    }

    setErrors({
      ...temp
    });
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value
    });
    validate({ [name]: value });
  };

  const handleCheckboxValue = (e) => {
    const { name, checked } = e.target;
    setValues({
      ...values,
      [name]: checked
    });
    validate({ [name]: checked });
  };

  const handleDropdownValue = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value
    });
    validate({ [name]: value });
  }

  const handleSuccess = () => {
    setValues({
      ...initialFormValues,
      formSubmitted: true,
      success: true
    });

    dispatch( setFormSubmitted() );

    // Send a message to the parent window upon successful form submission
    window.parent.postMessage({ event: 'formSubmitted', data: {...initialFormValues}}, '*');

    // Track form submission event in GTM
    TagManager.dataLayer({
      dataLayer: {
        event: 'formSubmitted',
        formName: 'SignupForm'
      }
    });
  };

  const handleError = (response) => {
    let temp = { ...errors };

    let errorDetails = response?.response?.data?.data?.details;
    let errorMessage = response?.response?.data?.data?.error;

    setValues({
      ...values,
      formSubmitted: false,
      success: false
    });

    const formatErrors = (errorArray) => {
      return errorArray.map((err, index) => {
        return (index > 0 ? '<br/>&bull; ' : '') + err;
      }).join('');
    };

    const fieldsToCheck = [
      { key: 'full_name', propName: 'fullName' },
      { key: 'company_name', propName: 'companyName' },
      { key: 'phone_number', propName: 'phoneNumber' },
      { key: 'business_email', propName: 'businessEmail' },
      { key: 'password', propName: 'password' },
    ];

    fieldsToCheck.forEach(field => {
      if (field.key in errorDetails) {
        temp[field.propName] = formatErrors(errorDetails[field.key]);
      }
    });

    setErrors({
      ...temp
    });

    setErrorMessage(errorMessage);
  };

  const formIsValid = (fieldValues = values) => {
    return fieldValues.fullName &&
      fieldValues.companyName &&
      fieldValues.phoneNumber &&
      fieldValues.businessEmail &&
      fieldValues.password &&
      fieldValues.acceptTos &&
      Object.values(errors).every((x) => x === "");
  };

  const handleFormSubmit = async ( setRefreshReCaptcha ) => {
    setRefreshReCaptcha(r => !r);
    const isValid = Object.values(errors).every((x) => x === "") && formIsValid();
    if (isValid) {
      dispatch( setOtpValidated(false) );
      await PostContactForm(values, handleSuccess, handleError);
    }
  };

  const handleCreateSaleLead = async () => {
    const hashedForm = SHA256( JSON.stringify( values ) ).toString();

    if ( getFormHash().payload !== hashedForm ) {
      dispatch( setFormHash( hashedForm ) );
      await PostContactSaleLead( values );
    }

  }

  const getPhoneNumber = () => {
    let dialString = getCountryInfo( values.dialCode );
    return dialString.dialCode + values.phoneNumber;
  }

  return {
    values,
    errors,
    errorMessage,
    handleInputValue,
    handleCheckboxValue,
    handleDropdownValue,
    handleFormSubmit,
    formIsValid,
    getPhoneNumber,
    handleCreateSaleLead
  };
}
